import { useState, useCallback, useMemo } from 'react';
import { QueryKey } from 'react-query';
import isArray from 'lodash/isArray';
import includes from 'lodash/includes';
import some from 'lodash/some';
import first from 'lodash/first';

import {
  UniversalFilterTextValue,
  UniversalFilterTextModes,
  UniversalFilterTextFilterState,
  UniversalFilterTextFilterOptions,
  UniversalFilterTextOnFilterStateChange,
  UniversalFilterTextOnChange
} from '../../UniversalFilterText.types';

import { useMountEffect } from '../../../../../../../common/hooks/useMountEffect';
import { useReactQueryState } from '../../../../../../../main/common/reactQuery/hooks/useReactQueryState';

import { useUniversalFilterTextFilterState } from '../useUniversalFilterTextFilterState';

import {
  UniversalFilterTemplateModeOption,
  UniversalFilterTemplateOnModeChange
} from '../../../../helpers/UniversalFilterTemplate';

import { filtersKeys } from '../../../../../../../locales/keys';

const defaultMode = UniversalFilterTextModes.CONTAINS;

const allModeOptions: UniversalFilterTemplateModeOption<UniversalFilterTextModes>[] =
  [
    {
      value: UniversalFilterTextModes.CONTAINS,
      i18nText: filtersKeys.modes.contains
    },
    {
      value: UniversalFilterTextModes.IS,
      i18nText: filtersKeys.modes.is
    },
    {
      value: UniversalFilterTextModes.IS_NOT,
      i18nText: filtersKeys.modes.isNot
    },
    {
      value: UniversalFilterTextModes.STARTS_WITH,
      i18nText: filtersKeys.modes.startsWith
    },
    {
      value: UniversalFilterTextModes.ENDS_WITH,
      i18nText: filtersKeys.modes.endsWith
    },
    {
      value: UniversalFilterTextModes.IS_EMPTY,
      i18nText: filtersKeys.modes.isEmpty
    },
    {
      value: UniversalFilterTextModes.IS_NOT_EMPTY,
      i18nText: filtersKeys.modes.isNotEmpty
    }
  ];

interface UniversalFilterTextOptions {
  filterStateCacheKey: QueryKey;
  filterValue?: UniversalFilterTextValue;
  options?: UniversalFilterTextFilterOptions;
  onChange: UniversalFilterTextOnChange;
}

function useUniversalFilterText({
  filterStateCacheKey,
  filterValue,
  options,
  onChange
}: UniversalFilterTextOptions) {
  const modeOptions = useMemo<
    UniversalFilterTemplateModeOption<UniversalFilterTextModes>[]
  >(() => {
    if (!isArray(options?.availableModes)) {
      return allModeOptions;
    }

    return allModeOptions.filter((modeOption) =>
      includes(options.availableModes, modeOption.value)
    );
  }, [options]);

  const { value: savedState, setValue: saveState } =
    useReactQueryState<UniversalFilterTextFilterState>(filterStateCacheKey);

  const { handleApplyFilterState, handleParseFilterValue } =
    useUniversalFilterTextFilterState({
      onChange
    });

  const [filterState, setFilterState] =
    useState<UniversalFilterTextFilterState>(
      savedState ||
        handleParseFilterValue(filterValue) || {
          mode: some(modeOptions, (option) => option.value === defaultMode)
            ? defaultMode
            : first(modeOptions)?.value,
          text: ''
        }
    );

  useMountEffect(() => {
    handleApplyFilterState(filterState);
  });

  const handleFilterStateChange =
    useCallback<UniversalFilterTextOnFilterStateChange>(
      (state) => {
        setFilterState(state);
        handleApplyFilterState(state);
        saveState(state);
      },
      [handleApplyFilterState, saveState]
    );

  const handleModeChange = useCallback<
    UniversalFilterTemplateOnModeChange<UniversalFilterTextModes>
  >(
    (mode) => {
      handleFilterStateChange({
        mode,
        text: filterState.text || ''
      });
    },
    [filterState, handleFilterStateChange]
  );

  const handleTextChange = useCallback<(text: string) => void>(
    (text) => {
      handleFilterStateChange({
        mode: filterState.mode,
        text: text || ''
      });
    },
    [filterState, handleFilterStateChange]
  );

  return {
    modeOptions,
    filterState,
    handleModeChange,
    handleTextChange
  };
}

export default useUniversalFilterText;
